import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiWifi, FiCreditCard, FiShield } = FiIcons;

function Footer() {
  return (
    <footer className="bg-slate-900 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <h3 className="text-2xl font-serif font-bold gradient-text">
              Yaman Restaurant & Guest House
            </h3>
            <p className="text-gray-300 text-sm leading-relaxed">
              Your trusted basecamp for Sinharaja Morning Side adventures. 
              Experience authentic Sri Lankan hospitality in the heart of Kolonna.
            </p>
            <div className="flex space-x-2">
              <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                Halal Certified
              </span>
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                Free WiFi
              </span>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="space-y-4"
          >
            <h4 className="text-lg font-semibold text-white">Quick Links</h4>
            <div className="space-y-2">
              <Link to="/" className="block text-gray-300 hover:text-emerald-400 transition-colors text-sm">
                Home
              </Link>
              <Link to="/menu" className="block text-gray-300 hover:text-emerald-400 transition-colors text-sm">
                Menu
              </Link>
              <Link to="/booking" className="block text-gray-300 hover:text-emerald-400 transition-colors text-sm">
                Booking
              </Link>
              <Link to="/guide" className="block text-gray-300 hover:text-emerald-400 transition-colors text-sm">
                Morning Side Guide
              </Link>
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="space-y-4"
          >
            <h4 className="text-lg font-semibold text-white">Contact</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-gray-300 text-sm">
                <SafeIcon icon={FiPhone} className="w-4 h-4 text-emerald-400" />
                <span>+94 77 123 4567</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-300 text-sm">
                <SafeIcon icon={FiMail} className="w-4 h-4 text-emerald-400" />
                <span>info@yamankolonna.com</span>
              </div>
              <div className="flex items-start space-x-2 text-gray-300 text-sm">
                <SafeIcon icon={FiMapPin} className="w-4 h-4 text-emerald-400 mt-0.5" />
                <span>Ullinduwawa, Kolonna<br />Ratnapura District, Sri Lanka</span>
              </div>
            </div>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="space-y-4"
          >
            <h4 className="text-lg font-semibold text-white">Services</h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-gray-300 text-sm">
                <SafeIcon icon={FiWifi} className="w-4 h-4 text-emerald-400" />
                <span>High-Speed WiFi (20Mbps)</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-300 text-sm">
                <SafeIcon icon={FiCreditCard} className="w-4 h-4 text-emerald-400" />
                <span>LankaQR & Card Payments</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-300 text-sm">
                <SafeIcon icon={FiShield} className="w-4 h-4 text-emerald-400" />
                <span>Secure Parking</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"
        >
          <div className="text-gray-400 text-sm">
            © 2025 Yaman Restaurant & Guest House. Designed and Created by Rajantha.
          </div>
          <div className="flex items-center space-x-6 text-sm">
            <span className="text-gray-400">Accepted Payments:</span>
            <div className="flex space-x-2">
              <span className="px-2 py-1 bg-gray-800 text-gray-300 rounded text-xs">Visa</span>
              <span className="px-2 py-1 bg-gray-800 text-gray-300 rounded text-xs">Mastercard</span>
              <span className="px-2 py-1 bg-gray-800 text-gray-300 rounded text-xs">LankaQR</span>
            </div>
          </div>
        </motion.div>

        {/* WhatsApp Float */}
        <motion.a
          href="https://wa.me/94771234567"
          target="_blank"
          rel="noopener noreferrer"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 1, type: "spring", stiffness: 260, damping: 20 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-lg transition-colors duration-300"
        >
          <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488z"/>
          </svg>
        </motion.a>
      </div>
    </footer>
  );
}

export default Footer;